import * as React from "react";
import {useEffect, useMemo} from "react";
import {Trans, useTranslation} from "react-i18next";
import window from "../../../lib/WindowExtended";
import {IIsVisible} from "../../Checkout";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import "./Finished.scss";
import {Payments} from "../../../lib/payments/PaymentController";
import ShopExperience from "./ShopExperience";
import {updateUrlParams} from "../../../cart/CartIcon/CartIconPortal";

interface IFinishedBoughtItems {
  hasVoucher: boolean;
  hasItems: boolean;
}

const Finished: React.FC<IIsVisible> = (props) => {
  const {t} = useTranslation("checkout");
  const {checkout} = useCheckoutContext();
  const orderNumber = checkout.orderId;
  const purchase = useMemo<IFinishedBoughtItems>(() => {
    return {
      hasItems:
        checkout.cartData.positions.find((position) => {
          return position.type === "Item" && position.item?.itemClassId !== 6;
        }) !== undefined,
      hasVoucher:
        checkout.cartData.positions.find((position) => {
          return position.type === "Item" && position.item?.itemClassId === 6;
        }) !== undefined,
    };
  }, []);

  useEffect(() => {
    if (props.isVisible) {
      window.amazonPayAuthorized = window.isBillingAgreementAddressSet = window.isBillingAgreementPaymentSet = false;
      window.orderReferenceId = "";
      updateUrlParams(null);
    }
    if (checkout.paymentId === Payments.AmazonPay && checkout.isCompleted) {
      cleanUrl();
    }
  }, [props.isVisible]);

  if (!props.isVisible) {
    return null;
  }

  const ThankYou: React.FC = () => {
    const {invoiceAddress} = checkout;

    if (!invoiceAddress) {
      return null;
    }

    const customerName = `${invoiceAddress.firstName} ${invoiceAddress.lastName}`;

    return <h1>{t("Vielen Dank, {{customerName}}!", {customerName})}</h1>;
  };

  const WhatsNextBox: React.FC = () => {
    if (!purchase.hasItems) {
      return null;
    }

    return (
      <div className="shippingsteps">
        <p className="shippingsteps__box">
          <span className="shippingsteps__box__icon Svg Svg-Icon-Mail">&nbsp;</span>
          <span className="shippingsteps__box__text">
            {t("Sie erhalten in Kürze eine E-Mail, die den Eingang Ihrer Bestellung bestätigt.")}
          </span>
        </p>
        <span className="shippingsteps__arrdown Svg Svg-Icon-ArrDown">&nbsp;</span>
        <p className="shippingsteps__box">
          <span className="shippingsteps__box__icon Svg Svg-Icon-Truck">&nbsp;</span>
          <span className="shippingsteps__box__text">{t("Immer informiert")}</span>
        </p>
        <span className="shippingsteps__arrdown Svg Svg-Icon-ArrDown">&nbsp;</span>
        <p className="shippingsteps__box">
          <span className="shippingsteps__box__icon Svg Svg-Icon-30">&nbsp;</span>
          <span className="shippingsteps__box__text">
            {t("Doch nicht zufrieden? Kein Problem! Wir bieten 30 Tage Rückgaberecht.")}
          </span>
        </p>
      </div>
    );
  };

  const AdditionalVoucherInformation: React.FC = () => {
    const infos: string[] = [];

    if (!purchase.hasVoucher) {
      return null;
    }

    infos.push(t("Ihren Geschenkgutschein erhalten Sie unmittelbar nach Zahlungseingang als PDF via E-Mail."));

    if (purchase.hasItems) {
      infos.push(
        " ",
        t(
          "Beim Kauf gemeinsam mit anderen Artikeln kann sich der Versand des Gutscheins bis zum nächsten Werktag verzögern.",
        ),
      );
    }

    return (
      <React.Fragment>
        <p className="shippingsteps__box">
          <span className="shippingsteps__box__icon Svg Svg-Icon-Mail">&nbsp;</span>
          <span className="shippingsteps__box__text">
            {infos.map((text) => {
              return text;
            })}
          </span>
        </p>
        <br className="clear" />
      </React.Fragment>
    );
  };

  return (
    <div className="text">
      <ThankYou />
      <p>
        <strong>{t("Ihre Bestellung war erfolgreich.")}</strong>
      </p>

      <p>
        <span className="svg svg-icon-check left" style={{marginBottom: "20px"}}>
          &#160;
        </span>
        {checkout.isGuest ? (
          t("Ihre Bestellnummer lautet {{orderNumber}}.", {orderNumber})
        ) : (
          <span>
            <Trans i18nKey={"bestellAbschlussMitNummer"} values={{orderNumber}} ns="checkout">
              Ihre Bestellung finden Sie unter der Nummer <b>{{orderNumber}}</b> in Ihrem{" "}
              <a href={t("userAccountLink")} target="_blank" rel="noreferrer">
                Kundenkonto
              </a>{" "}
              wieder.
            </Trans>
          </span>
        )}
      </p>

      <br className="clear" />

      <p>
        <strong>{t("So geht es weiter")}</strong>
      </p>

      <WhatsNextBox />
      <AdditionalVoucherInformation />
      {window.exponea && <ShopExperience />}

      {/* Sovendus-Container (zugehöriges JS wird im GTM eingebunden) */}
      <div>
        <div id="sovendus-container-1">{/* Empty */}</div>
        {/* TODO GTM greift nicht, weil es dynamisch reingeladen wird*/}
      </div>
    </div>
  );
};

export default Finished;

export const cleanUrl = (): void => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  urlSearchParams.delete("AuthenticationStatus");
  urlSearchParams.delete("gotoStep");
  urlSearchParams.delete("error");
  urlSearchParams.delete("view");
  const currentPage = window.location.href.split("?")[0];
  const cleanedUpUrlParams = urlSearchParams.toString() !== "" ? "?" + urlSearchParams.toString() : "";
  history.replaceState("", "", currentPage + cleanedUpUrlParams);
};
